<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { ArrowUp } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ChatComposerOption {
  value: string
  label: string
  testId?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    disabled?: boolean
    canSend?: boolean
    sendTitle?: string
    minHeight?: number
    maxHeightRatio?: number
    maxHeightPx?: number
    autoFocusInput?: boolean
    textareaTestId?: string
    sendTestId?: string
    panelClass?: string
    textareaClass?: string
    options?: ChatComposerOption[]
    selectedOption?: string | null
  }>(),
  {
    placeholder: 'Ask a question...',
    disabled: false,
    canSend: false,
    sendTitle: 'Send (Enter) • Shift+Enter for new line',
    minHeight: 44,
    maxHeightRatio: 0.32,
    maxHeightPx: 320,
    autoFocusInput: false,
    textareaTestId: undefined,
    sendTestId: undefined,
    panelClass: '',
    textareaClass: '',
    options: () => [],
    selectedOption: null
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  submit: []
  selectOption: [value: string]
}>()

const messageTextareaRef = ref<HTMLTextAreaElement | null>(null)

const model = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
})

const getComposerMaxHeight = () => {
  if (typeof window === 'undefined') return props.maxHeightPx
  return Math.min(Math.floor(window.innerHeight * props.maxHeightRatio), props.maxHeightPx)
}

const resizeComposer = () => {
  const textarea = messageTextareaRef.value
  if (!textarea) return

  const maxHeight = getComposerMaxHeight()
  textarea.style.height = '0px'
  const nextHeight = Math.max(props.minHeight, Math.min(textarea.scrollHeight, maxHeight))
  textarea.style.height = `${nextHeight}px`
  textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
}

const handleSubmit = () => {
  if (!props.canSend) return
  emit('submit')
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSubmit()
  }
}

const handleSelectOption = (value: string) => {
  emit('selectOption', value)
}

const focusComposerInput = () => {
  if (!props.autoFocusInput || props.disabled) return
  messageTextareaRef.value?.focus()
}

const handleOptionChange = (event: Event) => {
  const target = event.target as HTMLSelectElement | null
  const value = target?.value
  if (!value) return
  handleSelectOption(value)
}

watch(
  () => props.modelValue,
  () => {
    void nextTick(() => {
      resizeComposer()
    })
  }
)

onMounted(() => {
  window.addEventListener('resize', resizeComposer)
  void nextTick(() => {
    resizeComposer()
    focusComposerInput()
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', resizeComposer)
})
</script>

<template>
  <div :class="cn('border-border bg-card rounded-xl border p-3 shadow-sm', panelClass)">
    <slot name="leading" />
    <textarea
      ref="messageTextareaRef"
      v-model="model"
      :data-testid="textareaTestId"
      :placeholder="placeholder"
      rows="1"
      :disabled="disabled"
      :class="
        cn(
          'text-foreground placeholder:text-muted-foreground max-h-[36vh] min-h-[44px] w-full resize-none bg-transparent text-sm leading-6 outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          textareaClass
        )
      "
      @input="resizeComposer"
      @keydown="handleKeyDown"
    />

    <div class="mt-2 flex items-center justify-between gap-2">
      <slot name="footer-left" />

      <div class="ml-auto flex items-center gap-2">
        <div v-if="options.length > 0" class="relative">
          <select
            data-testid="chat-composer-mode-select"
            class="border-border bg-background text-foreground hover:bg-muted focus:ring-primary/20 h-7 min-w-[120px] appearance-none rounded-md border pr-8 pl-2.5 text-xs outline-hidden transition-colors focus:ring-2"
            :value="selectedOption ?? options[0]?.value"
            @change="handleOptionChange"
          >
            <option
              v-for="option in options"
              :key="option.value"
              :data-testid="option.testId"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <span
            class="text-muted-foreground pointer-events-none absolute inset-y-0 right-2 flex items-center text-[10px]"
          >
            v
          </span>
        </div>

        <Button
          :data-testid="sendTestId"
          :class="
            cn(
              'flex h-7 w-7 items-center justify-center rounded-full p-0 transition-colors',
              canSend
                ? 'bg-foreground text-background hover:bg-foreground/90'
                : 'bg-muted text-muted-foreground'
            )
          "
          :disabled="!canSend"
          :title="sendTitle"
          @click="handleSubmit"
        >
          <ArrowUp class="h-4 w-4" />
        </Button>
      </div>
    </div>
  </div>
</template>
